import { createRoute, notFound, redirect } from "@tanstack/react-router";
import { useAuthStore } from "@/lib/Auth/authStore";
import { rootRoute } from "@/routes/root-route";

/**
 * First path segment of every section that existed in the old unscoped app.
 * Only these are treated as stale bookmarks; any other unscoped path is a
 * genuinely unknown route → 404 (instead of being silently redirected).
 */
const LEGACY_SECTIONS = new Set([
  "bookings",
  "suppliers",
  "invoices",
  "vehicles",
  "users",
  "organisations",
  "payments",
  "pro-drivers",
  "offices",
  "cities",
  "contacts",
  "relocations",
  "gap-rentals",
  "reports",
  "import",
  "quotes",
  "emails",
  "blogs",
  "blog-authors",
  "blog-tags",
  "settings",
  "profile",
  "waitlists",
]);

/**
 * Catch-all for old, unscoped bookmarks/tabs (e.g. `/relocations`,
 * `/suppliers/123`) from before org/supplier moved into the URL. Without a
 * persisted org we can't rebuild the exact scoped path, so we send them through
 * the org picker. Already-scoped (`/org/...`, `/admin/...`) paths and unknown
 * sections that land here are genuinely unknown → 404.
 */
export const legacyRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  beforeLoad: ({ location }) => {
    const path = location.pathname;

    // Already-scoped paths (either tenant prefix) that reach this catch-all are
    // genuinely unknown routes → 404, not legacy bookmarks.
    if (path.startsWith("/org/") || path.startsWith("/admin/")) {
      throw notFound();
    }

    // Only treat paths whose first segment is a real legacy section as stale
    // bookmarks; anything else (mistyped/removed URLs) is a genuine 404.
    const firstSegment = path.split("/")[1] ?? "";
    if (!LEGACY_SECTIONS.has(firstSegment)) {
      throw notFound();
    }

    if (!useAuthStore.getState().token) {
      throw redirect({ to: "/sign-in" });
    }
    throw redirect({ to: "/org" });
  },
});
